import api from './axios';
import { ApiResponse, Notice } from '../types';

// Transform backend response (snake_case) to frontend format (camelCase)
const transformNotice = (backendNotice: any): Notice => {
  return {
    id: backendNotice._id || backendNotice.id,
    title: backendNotice.title,
    description: backendNotice.description,
    date: backendNotice.date,
    isCurrent: backendNotice.is_current !== undefined ? backendNotice.is_current : backendNotice.isCurrent,
    createdAt: backendNotice.createdAt || backendNotice.created_at,
    updatedAt: backendNotice.updatedAt || backendNotice.updated_at,
  };
};

const transformNotices = (notices: any[]): Notice[] => {
  return notices.map(transformNotice);
};

// Public endpoints (authenticated users)

export const getNotices = async (): Promise<ApiResponse<Notice[]>> => {
  const response = await api.get<ApiResponse<any[]>>('/api/v1/notices');
  return {
    ...response.data,
    data: transformNotices(response.data.data || []),
  };
};

export const getCurrentNotice = async (): Promise<ApiResponse<Notice | null>> => {
  const response = await api.get<ApiResponse<any | null>>('/api/v1/notices/current');
  return {
    ...response.data,
    data: response.data.data ? transformNotice(response.data.data) : null,
  };
};

export const getOldNotices = async (
  page: number = 1,
  limit: number = 6
): Promise<ApiResponse<{ notices: Notice[]; total: number; page: number; totalPages: number }>> => {
  try {
    const response = await api.get<any>(
      '/api/v1/notices/old',
      {
        params: { page, limit },
      }
    );
    
    console.log('getOldNotices API Response (full):', response);
    console.log('getOldNotices API Response (data):', response.data);
    console.log('getOldNotices API Response (data type):', typeof response.data);
    
    // Handle different response structures
    const responseData = response.data;
    let noticesData: any[] = [];
    let total = 0;
    let currentPage = page;
    let totalPages = 1;

    if (responseData) {
      console.log('getOldNotices - responseData keys:', Object.keys(responseData));
      
      if (responseData.data) {
        console.log('getOldNotices - responseData.data keys:', Object.keys(responseData.data));
        console.log('getOldNotices - responseData.data type:', typeof responseData.data);
        
        // Standard ApiResponse format: { success: true, data: { notices: [], total: X, ... } }
        if (responseData.data.notices && Array.isArray(responseData.data.notices)) {
          console.log('getOldNotices - Found notices array in data.data.notices');
          noticesData = responseData.data.notices;
          total = responseData.data.total || noticesData.length;
          currentPage = responseData.data.page || page;
          totalPages = responseData.data.totalPages || responseData.data.total_pages || Math.ceil(total / limit);
        } else if (Array.isArray(responseData.data)) {
          console.log('getOldNotices - data is directly an array');
          noticesData = responseData.data;
          total = noticesData.length;
          totalPages = Math.ceil(total / limit);
        } else {
          console.warn('getOldNotices - Unexpected data structure:', responseData.data);
        }
      } else if (Array.isArray(responseData)) {
        console.log('getOldNotices - responseData is directly an array');
        noticesData = responseData;
        total = noticesData.length;
        totalPages = Math.ceil(total / limit);
      } else if (responseData.notices && Array.isArray(responseData.notices)) {
        console.log('getOldNotices - Found notices at root level');
        noticesData = responseData.notices;
        total = responseData.total || noticesData.length;
        currentPage = responseData.page || page;
        totalPages = responseData.totalPages || responseData.total_pages || Math.ceil(total / limit);
      } else {
        console.warn('getOldNotices - Could not find notices array. ResponseData:', responseData);
      }
    } else {
      console.warn('getOldNotices - No responseData');
    }

    console.log('getOldNotices - Extracted notices (raw):', noticesData);
    console.log('getOldNotices - Extracted notices count:', noticesData.length);
    console.log('getOldNotices - Total:', total, 'Page:', currentPage, 'TotalPages:', totalPages);
    
    const transformedNotices = transformNotices(noticesData);
    console.log('getOldNotices - Transformed notices:', transformedNotices);
    console.log('getOldNotices - Transformed notices count:', transformedNotices.length);

    return {
      success: responseData?.success !== false,
      message: responseData?.message,
      data: {
        notices: transformedNotices,
        total,
        page: currentPage,
        totalPages,
      },
    };
  } catch (error: any) {
    console.error('getOldNotices - Error caught:', error);
    console.error('getOldNotices - Error response:', error.response);
    console.error('getOldNotices - Error status:', error.response?.status);
    console.error('getOldNotices - Error data:', error.response?.data);
    throw error;
  }
};

// Admin endpoints

export type CreateNoticePayload = {
  title: string;
  description: string;
  date?: string; // ISO date string, optional
  isCurrent?: boolean; // Optional, defaults to false
};

export type UpdateNoticePayload = {
  title?: string;
  description?: string;
  date?: string;
  isCurrent?: boolean;
};

export type NoticeFilters = {
  page?: number;
  limit?: number;
  isCurrent?: boolean | null; // null = all, true = current only, false = old only
};

export const getAdminNotices = async (
  filters: NoticeFilters = {}
): Promise<ApiResponse<{ notices: Notice[]; total: number; page: number; totalPages: number }>> => {
  // Convert camelCase filter to snake_case for backend
  const backendFilters: any = {
    page: filters.page || 1,
    limit: filters.limit || 10,
  };
  // Handle filter: null = all, true = current only, false = archived only
  // Backend expects: is_current=true for current, is_current=false for archived, or omit for all
  if (filters.isCurrent === true) {
    backendFilters.is_current = 'true'; // Send as string to ensure it's not lost
  } else if (filters.isCurrent === false) {
    backendFilters.is_current = 'false'; // Send as string to ensure it's not lost
  }
  // If isCurrent is null, don't send the filter (backend returns all)
  
  console.log('getAdminNotices - Frontend filters:', filters);
  console.log('getAdminNotices - Backend filters:', backendFilters);
  console.log('getAdminNotices - Filter isCurrent value:', filters.isCurrent, 'Type:', typeof filters.isCurrent);
  console.log('getAdminNotices - URL will be:', '/api/v1/admin/notices', 'with params:', backendFilters);

  // Build query string manually to ensure boolean values are sent correctly
  const queryParams = new URLSearchParams();
  queryParams.append('page', String(backendFilters.page || 1));
  queryParams.append('limit', String(backendFilters.limit || 10));
  if (backendFilters.is_current !== undefined) {
    queryParams.append('is_current', String(backendFilters.is_current));
  }
  
  console.log('getAdminNotices - Query string:', queryParams.toString());
  
  const response = await api.get<any>(
    `/api/v1/admin/notices?${queryParams.toString()}`
  );
  
  // Debug logging
  console.log('Admin Notices API Response (full):', response);
  console.log('Admin Notices API Response (data):', response.data);
  
  // Handle different response structures
  const responseData = response.data;
  let noticesData: any[] = [];
  let total = 0;
  let page = filters.page || 1;
  let totalPages = 1;

  // Check if response.data exists and has the expected structure
  if (responseData) {
    if (responseData.data) {
      // Standard ApiResponse format: { success: true, data: { notices: [], total: X, ... } }
      if (responseData.data.notices && Array.isArray(responseData.data.notices)) {
        noticesData = responseData.data.notices;
        total = responseData.data.total || noticesData.length;
        page = responseData.data.page || filters.page || 1;
        totalPages = responseData.data.totalPages || responseData.data.total_pages || Math.ceil(total / (filters.limit || 10));
      } else if (Array.isArray(responseData.data)) {
        // If data is directly an array
        noticesData = responseData.data;
        total = noticesData.length;
        totalPages = Math.ceil(total / (filters.limit || 10));
      }
    } else if (Array.isArray(responseData)) {
      // If response.data is directly an array
      noticesData = responseData;
      total = noticesData.length;
      totalPages = Math.ceil(total / (filters.limit || 10));
    } else if (responseData.notices && Array.isArray(responseData.notices)) {
      // If notices are at the root level
      noticesData = responseData.notices;
      total = responseData.total || noticesData.length;
      page = responseData.page || filters.page || 1;
      totalPages = responseData.totalPages || responseData.total_pages || Math.ceil(total / (filters.limit || 10));
    }
  }

  console.log('Extracted notices array:', noticesData);
  console.log('Pagination info:', { total, page, totalPages });

  return {
    success: responseData.success !== false,
    message: responseData.message,
    data: {
      notices: transformNotices(noticesData),
      total,
      page,
      totalPages,
    },
  };
};

export const getAdminNotice = async (id: string): Promise<ApiResponse<Notice>> => {
  const response = await api.get<ApiResponse<any>>(`/api/v1/admin/notices/${id}`);
  return {
    ...response.data,
    data: transformNotice(response.data.data),
  };
};

export const createNotice = async (data: CreateNoticePayload): Promise<ApiResponse<Notice>> => {
  // Convert camelCase to snake_case for backend
  const backendData: any = {
    title: data.title,
    description: data.description,
    date: data.date,
  };
  if (data.isCurrent !== undefined) {
    backendData.is_current = data.isCurrent;
  }

  const response = await api.post<ApiResponse<any>>('/api/v1/admin/notices', backendData);
  return {
    ...response.data,
    data: transformNotice(response.data.data),
  };
};

export const updateNotice = async (id: string, data: UpdateNoticePayload): Promise<ApiResponse<Notice>> => {
  // Convert camelCase to snake_case for backend
  const backendData: any = {};
  if (data.title !== undefined) backendData.title = data.title;
  if (data.description !== undefined) backendData.description = data.description;
  if (data.date !== undefined) backendData.date = data.date;
  if (data.isCurrent !== undefined) backendData.is_current = data.isCurrent;

  console.log('updateNotice - Sending data:', { id, backendData });
  
  const response = await api.put<any>(`/api/v1/admin/notices/${id}`, backendData);
  
  console.log('updateNotice - Response:', response.data);
  
  // Handle different response structures
  const responseData = response.data;
  let noticeData: any = null;

  if (responseData) {
    if (responseData.data) {
      noticeData = responseData.data;
    } else if (responseData.id || responseData._id) {
      // If notice is at root level
      noticeData = responseData;
    }
  }

  if (!noticeData) {
    throw new Error('Invalid response format from update notice API');
  }

  return {
    success: responseData.success !== false,
    message: responseData.message,
    data: transformNotice(noticeData),
  };
};

export const deleteNotice = async (id: string): Promise<ApiResponse<void>> => {
  const response = await api.delete<ApiResponse<void>>(`/api/v1/admin/notices/${id}`);
  return response.data;
};
